import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { authFetch } from "../lib/auth";
import { API } from "../App";

const MAX_POLLS = 10;
const POLL_INTERVAL = 2000;

const SubscriptionSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("checking"); // checking, paid, failed, timeout
  const attemptsRef = useRef(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }

    const poll = async () => {
      attemptsRef.current += 1;
      try {
        const res = await authFetch(
          `${API}/subscription/checkout/status/${sessionId}`
        );
        if (res.ok) {
          const data = await res.json();
          if (data.payment_status === "paid") {
            setStatus("paid");
            return;
          }
          if (data.status === "expired") {
            setStatus("failed");
            return;
          }
        }
      } catch (e) {
        // silent retry
      }

      if (attemptsRef.current >= MAX_POLLS) {
        setStatus("timeout");
        return;
      }

      timerRef.current = setTimeout(poll, POLL_INTERVAL);
    };

    poll();
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6" data-testid="subscription-success-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full"
      >
        <Card className="bg-card border-border">
          <CardContent className="p-10 text-center">
            {status === "checking" && (
              <>
                <Loader2 className="w-14 h-14 text-primary animate-spin mx-auto mb-6" />
                <h1 className="font-heading text-2xl font-semibold mb-2">
                  Confirming your subscription…
                </h1>
                <p className="text-muted-foreground">
                  This usually takes a few seconds.
                </p>
              </>
            )}
            {status === "paid" && (
              <>
                <div className="w-14 h-14 bg-primary/15 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle2 className="w-8 h-8 text-primary" />
                </div>
                <h1 className="font-heading text-2xl font-semibold mb-2">
                  You're all set!
                </h1>
                <p className="text-muted-foreground mb-8">
                  Your MindForge subscription is now active. Time to train.
                </p>
                <Button
                  onClick={() => navigate("/dashboard")}
                  data-testid="back-to-dashboard-btn"
                  className="w-full bg-primary text-primary-foreground hover:bg-primary/90 py-6"
                >
                  Go to Dashboard
                </Button>
              </>
            )}
            {(status === "failed" || status === "timeout") && (
              <>
                <div className="w-14 h-14 bg-destructive/15 rounded-full flex items-center justify-center mx-auto mb-6">
                  <XCircle className="w-8 h-8 text-destructive" />
                </div>
                <h1 className="font-heading text-2xl font-semibold mb-2">
                  {status === "timeout" ? "Still processing…" : "Payment not completed"}
                </h1>
                <p className="text-muted-foreground mb-8">
                  {status === "timeout"
                    ? "Your payment is taking longer than usual. We'll activate it as soon as Stripe confirms — feel free to refresh in a minute."
                    : "We couldn't confirm your payment. You can try again or contact support."}
                </p>
                <div className="flex flex-col gap-3">
                  <Button
                    onClick={() => navigate("/paywall")}
                    className="w-full bg-primary text-primary-foreground hover:bg-primary/90 py-6"
                  >
                    Try again
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => navigate("/dashboard")}
                  >
                    Back to dashboard
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default SubscriptionSuccess;
